import {ChangeEventHandler, FC, useCallback, useMemo, useState} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';

import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Select from '@/components/form/select';
import {SupportedLocale} from '@/initializers/i18next';
import {AdvisorRole, UserProfile} from '@/repositories/beneva-api/endpoints/users';
import {formatFullName} from '@/utils/document/format-name';

export type AdvisorCodes = {
  dealerCode: string;
  repCode: string;
};

export type AdvisorOption = {
  label: string;
  value: string;
  codes: {
    label: string;
    value: AdvisorCodes;
  }[];
};

type Props = {
  profile: UserProfile;
  onChange: (codes: AdvisorCodes | null) => void;
};

export const AdvisorCodeStep: FC<Props> = ({profile, onChange}) => {
  const {t, i18n} = useTranslation('common', {keyPrefix: 'components.new-document-creation.dialog.content'});

  const advisorOptions = useMemo(
    () => mapProfileToAdvisorOptions(profile, i18n.language as SupportedLocale),
    [profile, i18n],
  );
  const prefilledAdvisor = useMemo(() => {
    if (profile.advisorRole === AdvisorRole.Advisor) {
      return advisorOptions[0];
    }
    return null;
  }, [advisorOptions, profile]);

  const [selectedAdvisorIndex, setSelectedAdvisorIndex] = useState<number | null>(() => {
    if (prefilledAdvisor) {
      return advisorOptions.findIndex(advisor => advisor.value === prefilledAdvisor.value);
    }
    return null;
  });

  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const onAdvisorChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    event => {
      const advisorIndex = advisorOptions.findIndex(advisor => advisor.value === event.target.value);

      if (advisorIndex !== selectedAdvisorIndex) {
        if (selectedCode) {
          onChange(null);
        }
        setSelectedAdvisorIndex(advisorIndex);
        setSelectedCode(null);
      }
    },
    [advisorOptions, onChange, selectedAdvisorIndex, selectedCode],
  );

  const onAdvisorCodeChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    event => {
      const selectedAdvisor = advisorOptions[selectedAdvisorIndex ?? 0];
      const selectedCode = selectedAdvisor.codes.find(code => code.value.repCode === event.target.value);

      if (!selectedCode) {
        onChange(null);
        setSelectedCode(null);
      } else {
        setSelectedCode(selectedCode.value.repCode);
        onChange({
          dealerCode: selectedCode.value.dealerCode,
          repCode: selectedCode.value.repCode,
        });
      }
    },
    [advisorOptions, onChange, selectedAdvisorIndex],
  );

  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-bold" data-testid="advisor-code-step-separator">
        {t('step-advisor-code.instructions')}
      </h3>

      <div className="flex w-full items-start gap-4">
        <FormControl isRequired={profile.advisorRole !== AdvisorRole.Advisor}>
          <FieldWrapper
            identifier="advisor-name"
            label={t('step-advisor-code.advisor-name.placeholder')}
            showAssistiveText={false}
          >
            <Select
              className="flex-1"
              id="advisor-code-general-code"
              data-testid="advisor-code-general-code"
              onChange={onAdvisorChange}
              defaultValue={prefilledAdvisor?.value}
              isReadonly={profile.advisorRole === AdvisorRole.Advisor}
            >
              <option value="">{t('step-advisor-code.advisor-name.placeholder')}</option>
              {advisorOptions.map(advisor => (
                <option key={advisor.value} value={advisor.value}>
                  {advisor.label}
                </option>
              ))}
            </Select>
          </FieldWrapper>
        </FormControl>

        <FormControl isRequired isDisabled={selectedAdvisorIndex === null}>
          <FieldWrapper
            identifier="advisor-code"
            label={t('step-advisor-code.advisor-code.placeholder')}
            showAssistiveText={false}
          >
            <Select
              className={clsx('flex-1', selectedAdvisorIndex === null && 'opacity-50')}
              id="advisor-code-alt-code"
              value={selectedCode ?? ''}
              onChange={onAdvisorCodeChange}
              disabled={selectedAdvisorIndex === null}
            >
              <option value="">{t('step-advisor-code.advisor-code.placeholder')}</option>
              {advisorOptions[selectedAdvisorIndex ?? 0]?.codes.map(code => (
                <option key={code.value.repCode} value={code.value.repCode}>
                  {code.label}
                </option>
              ))}
            </Select>
          </FieldWrapper>
        </FormControl>
      </div>
    </div>
  );
};

const mapProfileToAdvisorOptions = (profile: UserProfile, locale: SupportedLocale): AdvisorOption[] => {
  switch (profile.advisorRole) {
    case AdvisorRole.Employee:
    case AdvisorRole.Advisor:
      return mapAdvisorProfileToAdvisorOptions(profile, locale);
    case AdvisorRole.Assistant:
      return mapAssistantProfileToAdvisorOptions(profile, locale);
    default:
      return [];
  }
};

const mapAdvisorProfileToAdvisorOptions = (profile: UserProfile, locale: SupportedLocale): AdvisorOption[] => {
  return [
    {
      label: formatFullName(profile.cons2_firstName, profile.cons1_lastName, {locale}),
      value: profile.cons9_generalAgentCode,
      codes: [
        profile.cons25_advisorCode,
        profile.cons12_altAdvisorCode1,
        profile.cons13_altAdvisorCode2,
        profile.cons14_altAdvisorCode3,
        profile.cons15_altAdvisorCode4,
      ]
        .filter((v): v is string => !!v)
        .map(code => ({
          label: `${profile.cons9_generalAgentCode}-${code}`,
          value: {
            dealerCode: profile.cons9_generalAgentCode,
            repCode: code,
          },
        })),
    },
  ];
};

const mapAssistantProfileToAdvisorOptions = (profile: UserProfile, locale: SupportedLocale): AdvisorOption[] => {
  return profile.delegatedAdvisors.map(advisor => ({
    label: formatFullName(advisor.firstName, advisor.lastName, {locale}),
    value: advisor.advisorId,
    codes: advisor.codes.map(code => {
      const [dealerCode, repCode] = code.split('-');
      return {
        label: code,
        value: {dealerCode, repCode},
      };
    }),
  }));
};
